import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface ConversionEvent {
  type: 'page_view' | 'button_click' | 'form_submit' | 'booking_start' | 'booking_complete' | 'signup' | 'contact';
  page: string;
  timestamp: number;
  source?: string;
  metadata?: Record<string, any>;
}

export const useConversionTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page views
    trackEvent({
      type: 'page_view',
      page: location.pathname,
      timestamp: Date.now(),
      source: document.referrer || 'direct',
    });
  }, [location.pathname]);

  const trackEvent = (event: ConversionEvent) => {
    // Store in localStorage for analytics
    const events = JSON.parse(localStorage.getItem('conversion_events') || '[]');
    events.push(event);
    
    // Keep only last 100 events
    if (events.length > 100) {
      events.shift();
    }
    
    localStorage.setItem('conversion_events', JSON.stringify(events));

    // Send to analytics if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event.type, {
        page_path: event.page,
        source: event.source,
        ...event.metadata,
      });
    }
  };

  const trackButtonClick = (buttonName: string, metadata?: Record<string, any>) => {
    trackEvent({
      type: 'button_click',
      page: location.pathname,
      timestamp: Date.now(),
      metadata: { buttonName, ...metadata },
    });
  };

  const trackBookingStart = (therapistId: string) => {
    trackEvent({
      type: 'booking_start',
      page: location.pathname,
      timestamp: Date.now(),
      metadata: { therapistId },
    });
  };

  const trackBookingComplete = (therapistId: string, amount: number) => {
    trackEvent({
      type: 'booking_complete',
      page: location.pathname,
      timestamp: Date.now(),
      metadata: { therapistId, amount },
    });
  };

  return {
    trackButtonClick,
    trackBookingStart,
    trackBookingComplete,
    trackEvent,
  };
};

export const ConversionTracking = () => {
  useConversionTracking();
  return null;
};
